import React, { useEffect, useRef, useState } from 'react';
import { buildConfig, Subtitle, SubtitleWord } from '@monorepo/remotion-ninja';
import { useVideoContext } from '../../../hooks/use-video';
import { useStore } from '../../../helpers/use-store';
import { useResponsiveReady } from '../../../hooks/use-responive-ready';
import { Languages, Video } from '@monorepo/types';
import { Tag, Typography, Space, Row, Col, Card, Button } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import EditWordModal, {
  EditWordModalActions,
} from './edit-word/edit-word-modal';

const { Text } = Typography;

const Captions = () => {
  const modalRef = useRef<EditWordModalActions>(null);
  const {
    dataStore: { musicStore },
  } = useStore();
  const { video, scenes, assets, playerRef, isPlayerReady, movePlayerToFrame } =
    useVideoContext();
  const [selectedSubtitleIndex, setSelectedSubtitleIndex] = useState(0);

  const music = musicStore.items.find(
    (music) => music._id === video?.backgroundMusicId
  );

  const config = buildConfig({
    backgroundMusicUrl: video?.backgroundMusicUrl
      ? video.backgroundMusicUrl
      : music?.audioUrl,
    backgroundMusicVolume: video?.backgroundMusicVolume,
    subtitlesConfig: video?.subtitlesConfig,
    video: video as Video,
    scenes,
    assets,
    language: video?.language as Languages,
  });

  // Get subtitles from config
  const subtitles = config.subtitles || [];

  // Scroll to selected subtitle when selectedSubtitleIndex changes
  useEffect(() => {
    const selectedSubtitle = document.querySelector(
      `.subtitle-item-${selectedSubtitleIndex}`
    );
    const fewNextSubs = document.querySelector(
      `.subtitle-item-${selectedSubtitleIndex + 2}`
    );

    const subToScroll = fewNextSubs || selectedSubtitle;

    if (subToScroll) {
      subToScroll.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [selectedSubtitleIndex]);

  useEffect(() => {
    if (isPlayerReady) {
      const updateSelected = (e: any) => {
        const seekedFrame = e.detail.frame;
        const seekedTime = seekedFrame / 30; // Convert frame to time (30 fps)

        // Find the subtitle that matches the current time
        const index = subtitles.findIndex(
          (subtitle) =>
            seekedTime >= subtitle.startTime && seekedTime <= subtitle.endTime
        );

        // Set the selected index if found, otherwise keep current selection
        if (index !== -1 && index !== selectedSubtitleIndex) {
          setSelectedSubtitleIndex(index);
        }
      };

      playerRef.current?.addEventListener('frameupdate', updateSelected);

      return () => {
        playerRef.current?.removeEventListener('frameupdate', updateSelected);
      };
    }
  }, [isPlayerReady, subtitles, selectedSubtitleIndex]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = (seconds % 60).toFixed(2);
    return `${minutes}:${remainingSeconds.padStart(5, '0')}`;
  };

  const onSubtitlesSelect = (subtitle: Subtitle) => {
    movePlayerToFrame(subtitle.startTime * 30, false);
  };

  const handleWordClick = (e, subtitle: SubtitleWord) => {
    e.stopPropagation(); // Prevent Card onClick from firing

    let startTime = subtitle.startTime;
    let endTime = subtitle.endTime;

    const firstScene = scenes[0];
    console.log(firstScene);

    if (
      video?.thumbnailConfig.enabled &&
      video.thumbnailConfig.enableVoiceover &&
      video.thumbnailConfig.voiceoverUrl
    ) {
      if (subtitle.sceneId === firstScene._id) {
        const thumbnailDurationInSeconds = video.thumbnailConfig.duration;

        startTime = Number((startTime - thumbnailDurationInSeconds).toFixed(3));
        endTime = Number((endTime - thumbnailDurationInSeconds).toFixed(3));
      }
    }
    modalRef.current?.open({
      word: subtitle.text,
      start: subtitle.startTime,
      end: subtitle.endTime,
      sceneId: subtitle.sceneId,
    });

    console.log(subtitle, startTime, endTime);
    // You can implement your edit functionality here
  };

  return (
    <div className="captions">
      {subtitles.map((subtitle, index) => {
        const isSelected = index === selectedSubtitleIndex;

        return (
          <Card
            className={`subtitle-item subtitle-item-${index}`}
            onClick={() => onSubtitlesSelect(subtitle)}
            hoverable={true}
            key={index}
            style={{
              marginBottom: '10px',
              borderColor: isSelected ? '#1890ff' : '#f0f0f0',
            }}
            bodyStyle={{
              padding: '12px',
              backgroundColor: isSelected ? '#f0f8ff' : 'white',
            }}
          >
            <Row align="middle" style={{ marginBottom: '8px' }}>
              <Col>
                <Space>
                  <ClockCircleOutlined style={{ color: '#8b949e' }} />
                  <Text style={{ color: '#595959' }}>
                    {formatTime(subtitle.startTime)} -{' '}
                    {formatTime(subtitle.endTime)}
                  </Text>
                </Space>
              </Col>
            </Row>

            <Space wrap>
              {subtitle.words.map((word, wordIndex) => {
                return (
                  <Button
                    key={wordIndex}
                    variant={'filled'}
                    color={'primary'}
                    size="small"
                    onClick={(e) => handleWordClick(e, word)}
                    className="editable-word"
                  >
                    {word.text}
                  </Button>
                );
              })}
            </Space>
          </Card>
        );
      })}
      <EditWordModal ref={modalRef} />
    </div>
  );
};

export default Captions;
